/**
 * The iOS and Android apps (Capacitor, Phase 5, 2026-10-16). Everything here does nothing in a
 * browser, so the web app is unchanged.
 *
 * - Sign-in with Google/Facebook/Apple opens in the system browser and comes back to the app on
 *   antnest://auth/callback (the web app's /oauth/callback finishes it, as on the web).
 * - Android's back button goes back in the app, and leaves the app on the first screen.
 * - A copy of what the app keeps in browser storage (the login) is saved natively when the app goes
 *   to the background, and put back at start if the system ever cleared the web view's storage.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const nativePlatform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/** Where Supabase sends the browser back to after a social sign-in. */
export const NATIVE_OAUTH_CALLBACK = 'antnest://auth/callback';

/** The redirect address for a social sign-in; `query` is kept (e.g. "intent=link&provider=google"). */
export function oauthRedirectTo(query = ''): string {
  const qs = query ? `?${query}` : '';
  return isNative ? `${NATIVE_OAUTH_CALLBACK}${qs}` : `${window.location.origin}/oauth/callback${qs}`;
}

/**
 * Opens a sign-in page. In the apps it goes to the system browser (Google does not allow sign-in
 * inside an app's own web view); on the web the page itself navigates.
 */
export async function openSignInPage(url: string): Promise<void> {
  if (!isNative) {
    window.location.assign(url);
    return;
  }
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'popover' });
}

/** The app path that finishes a sign-in, from the link the system browser came back with. */
export function callbackPathFrom(link: string): string | null {
  if (!link.startsWith(NATIVE_OAUTH_CALLBACK)) return null;
  const rest = link.slice(NATIVE_OAUTH_CALLBACK.length);
  if (rest && !/^[?#]/.test(rest)) return null;
  return `/oauth/callback${rest}`;
}

// ── Login backup ───────────────────────────────────────────────────────────────────────────────
const BACKUP_KEY = 'antnest_web_storage_backup';

/** Keys worth keeping: the Supabase login and the app's own (family_task_manager_*, ftm_*). */
export const backedUpKey = (k: string) => /^(sb-|family_task_manager_|ftm_|antnest:)/.test(k);

export function snapshotStorage(storage: Storage): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && backedUpKey(k)) out[k] = storage.getItem(k) ?? '';
  }
  return out;
}

/** Puts back only what is missing — never overwrites what the web view still has. */
export function restoreStorage(storage: Storage, backup: Record<string, string>): number {
  let n = 0;
  for (const [k, v] of Object.entries(backup)) {
    if (backedUpKey(k) && storage.getItem(k) === null) { storage.setItem(k, v); n++; }
  }
  return n;
}

async function saveBackup() {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: BACKUP_KEY, value: JSON.stringify(snapshotStorage(window.localStorage)) });
  } catch { /* best effort */ }
}

/** Runs before the app renders (index.tsx). */
export async function startNative(): Promise<void> {
  if (!isNative) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: BACKUP_KEY });
    if (value) restoreStorage(window.localStorage, JSON.parse(value));
  } catch { /* first start, or nothing to restore */ }

  const { App } = await import('@capacitor/app');
  App.addListener('appStateChange', ({ isActive }) => { if (!isActive) void saveBackup(); });
  App.addListener('backButton', ({ canGoBack }) => { if (canGoBack) window.history.back(); else void App.exitApp(); });
  App.addListener('appUrlOpen', async ({ url }) => {
    const path = callbackPathFrom(url);
    if (!path) return;
    try { (await import('@capacitor/browser')).Browser.close(); } catch { /* already closed */ }
    // A fresh load of the callback page finishes the sign-in exactly as on the web.
    window.location.assign(path);
  });
  window.setInterval(() => void saveBackup(), 60_000);
}

/** After the first render: hide the splash screen. */
export async function nativeReady(): Promise<void> {
  if (!isNative) return;
  try { (await import('@capacitor/splash-screen')).SplashScreen.hide(); } catch { /* fine */ }
}
